<template>
    <div class="h--100 relative min-h-inherit">
        <div :class="{ 'disabled': deleteLoading, 'min-h-inherit': !invoices.length }">
            <div class="page--wrap">
                <template v-if="pageLoading">
                    <PageSpinner />
                </template>
    
                <template v-else-if="fetchInvoicesErr">
                    <EmptyPage state="error" @action="handleFetchInvoices({}, true)" />
                </template>
    
                <template v-else>
                    <div class="flex align-items-center justify-content-between width-100">
                        <div v-if="showActionBtns">
                            <p class="table__striped__footer__results">
                                COUNT: <span>{{ invoices.length }}</span>
                            </p>
                        </div>

                        <template v-if="showActionBtns">
                            <div class="flex align-items-center gap-2">
                                <div v-if="selectedInvoices.length > 0">
                                    <BaseButton variant="warning" is-content @submit="toggleDelete">
                                        <template #content>
                                            Delete {{ selectedInvoices.length }} {{ selectedInvoices.length === 1 ? 'invoice' : 'invoices' }}
                                        </template>
                                    </BaseButton>
                                </div>
                                <DateRangeFilter page="invoices" />
                                <MainTwo page-type="invoices" />
                                <BaseButton 
                                    label="+ Add invoice" 
                                    @submit="initiateCreate"
                                />
                            </div>
                        </template>
                    </div>
    
                    <div class="w-100" :class="{ 'min-h-inherit justify-content-center flex align-items-center': !invoices.length }">
                        <template v-if="!invoices.length">
                            <EmptyPage v-if="isSearched" state="no_result" />
                            <EmptyPage 
                                v-else 
                                state="empty_custom" 
                                icon-name="receipt" 
                                type="invoice" 
                                btn-state="custom"
                                position="left"
                                title="No invoices created" 
                                subtitle="Create, send, track, and manage invoices here. To get started, ensure you have created at least one active client and project."
                            >
                                <template #button>
                                    <BaseButton 
                                        label="+ New invoice" 
                                        :show-spinner="false"
                                        @submit="initiateCreate"
                                    />
                                </template>
                            </EmptyPage>
                        </template>
                        
                        <template v-else>
                            <div class="flex mt--40">
                                <CustomTable
                                    :columns="invoiceHeaders"
                                    :data="invoices"
                                    row-type="link"
                                    :selected-for-deletion="selectedInvoices"
                                    router-name="invoice-details-view"
                                    is-action-btns table-type="striped"
                                    @onRowClick="goToInvoiceDetailsPage"
                                    @onRowSelected="selectInvoices"
                                >
                                    <template #rows="data">
                                        <td class="first td-text-wrap">
                                            {{ data?.client?.name || "N/A" }}
                                        </td>
                                        <td class="first td-text-wrap">
                                            {{ data?.client?.email || "N/A" }}
                                        </td>
                                        <td>
                                            <Status type="invoice" :value="data.status" />
                                        </td>
                                        <td class="text--upper">
                                            {{ getSymbol(data.currency).symbol }}{{ $format_number(data.totalAmount) }}
                                        </td>
                                        <td>#{{ data.invoiceNo }}</td>
                                        <td>{{ $format_date(data.dueDate) }}</td>
                                        <td>{{ $format_date(data.createdAt) || "-" }}</td>
                                    </template>
                                </CustomTable>
                            </div>
    
                            <div class="table__striped__footer mt--5">
                                <pagination v-if="showPagination" :pagination-data="pageData" />
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </div>

        <ConfirmDeletion
            :show-modal="showDeleteModal"
            type="invoice" custom-msg
            :items="selectedInvoices"
            :loading="deleteLoading" 
            @cancel="showDeleteModal = false"
            @delete="deleteMultipleInvoices"
        >
            <template #custom-msg>
                <p class="text--sm">
                    These invoice(s) will be removed from all related projects and clients. <strong>This action cannot be undone.</strong>
                </p>
            </template>
        </ConfirmDeletion>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModal|| showUpgradeModalAlt" 
            type="invoices" 
            @cancel="closeUpgradeModal" 
        />
    </div>
</template>

<script>
import {
    Pagination,
    EmptyPage,
    ConfirmDeletion,
    PageSpinner,
    BaseButton,
    CustomTable,
    Status,
    UpgradeAccountModal,
    DateRangeFilter,
} from "@/components/ui/index";
import { assembleQueryList } from "@/utils/helpers";
import { invoiceHeaders } from "@/utils/dummy";
import { invoiceStatusMap, MAX_COUNT_BY_COLLECTIONS } from "@/utils/enums";
import { supportedCurrenciesMap } from "@/plugins/countries";
import MainTwo from "@/components/ui/filter/MainTwo.vue";

export default {
    name: "InvoicesPage",

    components: {
        CustomTable,
        ConfirmDeletion,
        EmptyPage,
        Pagination,
        PageSpinner,
        BaseButton,
        Status,
        UpgradeAccountModal,
        MainTwo,
        DateRangeFilter,
    },

    data() {
        return {
            displayType: "",
            selectedInvoices: [],
            deleteLoading: false,
            pageLoading: false,
            fetchInvoicesErr: false,
            fetchInvoicesErrMsg: "",
            invoiceHeaders,
            invoiceStatusMap,
            showDeleteModal: false,
            showUpgradeModalAlt: false,
            lastFetchKey: null,
            fetchDebounceTimer: null,
        };
    },

    computed: {
        pageData() {
            return this.$store.getters["invoice/pageData"];
        },

        invoices() {
            return this.$store.getters["invoice/invoices"] || [];
        },

        showActionBtns() {
            if (this.pageData?.total) {
                return true;
            }

            if (this.isSearched) {
                return true;
            }
            
            return false;
        },

        showPagination() {
            if (this.pageData?.totalPages > 1) {
                return true;
            } else {
                return false;
            }
        },

        refreshList() {
            return this.$store.getters["project/refreshList"];
        },

        showUpgradeModal() {
            return this.$store.getters["app/showUpgradeModal"];
        },

        checkUpgradeLoading() {
            return this.$store.getters["app/checkUpgradeLoading"];
        },

        checkUpgradeFields() {
            return this.$store.getters["app/checkUpgradeFields"];
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        routeFilters() {
            const query = this.$route.query;
            const filters = {};
            const validFilters = ["status", "invoiceNo", "currency", "endDate", "range", "filterBy", "startDate"];
      
            validFilters.forEach(filter => {
                if (query[filter]) {
                    filters[filter] = query[filter];
                }
            });
      
            return filters;
        },

        isSearched() {
            return Object.keys(this.routeFilters).length > 0;
        },
    },

    watch: {
        "$route.query": {
            async handler(newVal) {
                console.log("run route query");
                await this.handleFetchInvoices(newVal, true);
            },
            deep: true,
        },
    },

    async mounted() {  
        if (!this.invoices?.length) {
            await this.handleFetchInvoices();
        }      
    },

    // beforeUnmount() {
    //     if (this.fetchDebounceTimer) {
    //         clearTimeout(this.fetchDebounceTimer);
    //         this.fetchDebounceTimer = null;
    //     }
    //     // Reset last fetch key to avoid stale skip on re-enter
    //     this.lastFetchKey = null;
    // },

    methods: {
        goToAccountSettings() {
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        goToInvoiceDetailsPage(row) {
            if (!row || !row._id) return;

            this.$router.push(`/dashboard/invoices/${row?._id}`);
        },
        
        getSymbol(currency = "") {
            if (!currency) return "$";

            const lowerCaseCurrency = currency?.toLowerCase();
            return supportedCurrenciesMap[lowerCaseCurrency] || "$";
        },

        async initiateCreate() {
            try {    
                if (["standard", "free"].includes(this.accountPlan)) {        
                    const maxLimit = MAX_COUNT_BY_COLLECTIONS[this.accountPlan].invoices?.create;
    
                    if (maxLimit !== 0 && this.invoices.length >= maxLimit) {
                        this.showUpgradeModalAlt = true;
                        return;
                    }    
                }
            
                // if (this.isSearched) {
                //     if (["standard", "free"].includes(this.accountPlan)) {        
                //         await this.$store.dispatch("app/checkUpgradeStatus", "invoices");
                //     }
                // }

                return this.$router.push("/dashboard/invoices/create");
            } catch (error) {
                // console.log({ error });
            }
        },

        closeUpgradeModal() {
            if (this.showUpgradeModalAlt) {
                this.showUpgradeModalAlt = false;
                return;
            }
            return this.$store.dispatch("app/closeUpgradeModal", "invoices");
        },

        toggleDelete() {
            this.showDeleteModal = !this.showDeleteModal;
        },

        async deleteMultipleInvoices() {
            try {
                this.deleteLoading = true;

                await this.$store.dispatch("invoice/deleteInvoices", this.selectedInvoices);

                const successMsg = this.selectedInvoices.length === 1 ? "Item deleted" : "Items deleted";

                this.$toast.success(successMsg);
                this.selectedInvoices = [];
                this.showDeleteModal = false;                
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message || "An error occurred";                
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
            }
        },

        selectInvoices(val) {            
            this.selectedInvoices = val;
        },

        debouncedFetchInvoices(params = {}, refresh = false) {
            if (this.fetchDebounceTimer) {
                clearTimeout(this.fetchDebounceTimer);
            }
            this.fetchDebounceTimer = setTimeout(() => {
                this.handleFetchInvoices(params, refresh);
            }, 300); // 300ms delay
        },

        async handleFetchInvoices(params = {}, refresh = true) {                        
            try {
                console.log({ params });
                
                // const currentKey = JSON.stringify({
                //     filters: this.routeFilters,
                //     page: this.$route.query.page || 1,
                //     type: this.$route.query.type || "all",
                //     ...params,
                // });

                // console.log({ currentKey, lastFetchKey: this.lastFetchKey });
                
                // // if (!refresh && this.lastFetchKey === currentKey) return;
                // if (this.lastFetchKey === currentKey) return;

                // const hasCached = this.$store.getters["invoice/hasCachedData"]({
                //     ...this.routeFilters,
                //     page: this.$route.query.page || 1,
                //     type: this.$route.query.type || "all",
                //     ...params,
                // });

                // console.log({ hasCached });

                // if (!refresh && hasCached) {
                //     this.lastFetchKey = currentKey;
                //     return;
                // }
                
                this.pageLoading = true;
                this.fetchInvoicesErr = false;
                this.fetchInvoicesErrMsg = "";

                const pageQueryObj = {
                    page: params?.page || 1,
                    type: params?.type || "all",
                    ...params,
                    ...this.routeFilters,
                };

                const buildResult = assembleQueryList(pageQueryObj);                
                await this.$store.dispatch("invoice/fetchInvoices", { params: buildResult, refresh });
                
                // this.lastFetchKey = currentKey;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;                                
                this.fetchInvoicesErr = true;
                this.fetchInvoicesErrMsg = errMsg;
            } finally {
                this.pageLoading = false;
            }
        },
    },
};
</script>